/**
 * #2496 stage 3 — the access-request queue, as the browser sees it.
 *
 * ## ⛔ EVERY PATH HERE IS `site-admin-api`'s PROXY PREFIX, NEVER `cp-auth-api`'s OWN
 *
 * The producer is `cp-auth-api`'s `@Controller('v1/admin/access-requests')`, and the browser cannot
 * reach it: `site-admin-api` owns `/v1/admin` at the gateway and mounts the same five paths,
 * forwarding the caller's own bearer token. The two spellings are identical here only because the
 * proxy chose to mirror the producer's mount — the same coincidence `identity.api.ts` records for
 * the invitation family, and the same reason a "simplification" toward the producer's controller is
 * the wrong edit rather than a harmless one.
 *
 * ## ⛔ THE VIEW IS DECLARED HERE AND MIRRORED BY HAND, BECAUSE IT HAS TO BE
 *
 * `AccessRequestView` lives in `cp-auth-api`'s DTO and in this file, spelled twice. The console
 * cannot import from another service's package, and the shape is not in `@zion/sdk-contracts` —
 * `InvitationView` is the precedent and was declared twice for the same reason. The mechanical link
 * is `__tests__/accessRequests.api.test.ts`, which pins the literal field list against the plan's
 * §2 interface map. ⚠️ `site-admin-api` types the proxied body `unknown` and validates with its own
 * zod schema, so it is not a third declaration of this interface and cannot be read as one.
 *
 * ## ⛔ THE THREE WRITES ANSWER **201**, AND NOTHING HERE MAY CHECK FOR 200
 *
 * Approve, deny and reopen are `POST`s with no explicit `@HttpCode`, so Nest answers 201 and the
 * `site-admin-api` proxy passes it through — the same shape `POST /v1/admin/users/:id/reactivate`
 * already has. The GET list and the GET summary answer 200.
 *
 * ⇒ success is `response.ok`, never `status === 200`, and `apiFetch` is already written that way.
 * ⛔ A `status === 200` check anywhere on this path would turn every successful approval into a
 * thrown `ApiError` carrying a 201 — a refusal the console would render from the status map, saying
 * something false about a write that worked. `accessRequests.api.test.ts` drives a 201 through both
 * of the writes this module owns so the claim is measured rather than assumed.
 *
 * ## ⛔ FOUR ROUTES, AND THAT IS THE MODULE'S WHOLE SURFACE
 *
 * List, summary, approve, deny. Every export below has a production reader in `PeoplePane` or in
 * `CpAdminApp`; there is nothing here for a screen that does not exist.
 *
 * ⛔ **REOPEN IS ABSENT ON PURPOSE, AND IT IS THE ONE A READER WILL REACH FOR.** The producer
 * declares `POST /:id/reopen` and `zion-ops` calls it — through `@zion/sdk-node`, never through this
 * module. D10 gives the People section the `pending` queue only, so there is no denied row on
 * screen to reopen and no control to put a client behind. ⇒ a `reopenAccessRequest()` here would be
 * a primitive with zero adopters, which this package refuses by ruling: *"a new primitive merges
 * with its adopters or it does not merge."* `PeoplePane.test.tsx` keeps a live MSW tripwire that
 * fails if the pane ever requests the route.
 *
 * ⚠️ Adding it back means adding the screen in the same change.
 */
import { api } from '../platform/http/client';
import { OPERATOR_INVITE_PERMISSION, OPERATOR_READ_PERMISSION, type UserRole } from './identity.api';

/**
 * The lifecycle, `access-request.dto.ts`'s `ACCESS_REQUEST_STATUSES`.
 *
 * ⛔ `pending` is the only one the console lists. `approved`, `denied` and `superseded` are
 * terminal and are read by status through {@link listAccessRequests}'s filter; `approved` in
 * particular can never co-exist with a pending row for the same address, because approval moves
 * the row out of `pending` in the same act that creates the operator (D10). `superseded` is the
 * answer of an approve or a deny on a row whose address already had an operator: the producer
 * closes the row and writes no operator and no role (issue #4715).
 */
export const ACCESS_REQUEST_STATUSES = ['pending', 'approved', 'denied', 'superseded'] as const;
export type AccessRequestStatus = (typeof ACCESS_REQUEST_STATUSES)[number];

/**
 * The two identity providers a request can arrive through.
 *
 * ⚠️ GitHub is `defaultEnabled: false` in the provider catalogue and no GitHub credential key
 * exists in any environment's Helm values, so every row in production today is `google`. The arm
 * ships because the producer's CHECK constraint carries both, and a column that renders one value
 * is not evidence the other cannot appear.
 */
export const ACCESS_REQUEST_PROVIDERS = ['google', 'github'] as const;
export type AccessRequestProvider = (typeof ACCESS_REQUEST_PROVIDERS)[number];

/**
 * One row of the queue.
 *
 * ⛔ `email` IS PROVIDER-ATTESTED AND VERBATIM. It is what Google or GitHub said the person's
 * address is, not anything the caller typed, and the producer stores it unmodified beside a
 * separate normalized column it matches on. ⇒ it is safe to render and it is the value
 * `DestructiveConfirm`'s typed challenge uses (D26), because it is the only thing on the row that
 * identifies a person.
 *
 * ⛔ `decidedBy` IS AN OPERATOR ID WITH NO FOREIGN KEY, so it can name an operator row that has
 * since been deleted. Nothing in the console renders it today; a surface that starts to must treat
 * a miss as *"an administrator"* rather than as an error.
 */
export interface AccessRequestView {
  readonly id: string;
  readonly email: string;
  readonly provider: AccessRequestProvider;
  readonly status: AccessRequestStatus;
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * The count behind the People rail item (D25).
 *
 * ⛔ A ROUTE OF ITS OWN, NOT `listAccessRequests().length`. The list ships up to 200 addresses to
 * render one integer, and every one of them is a person's email — so the cheap-looking derivation
 * is also the one that puts the whole queue in a response nobody reads (S-6).
 */
export interface AccessRequestSummary {
  readonly pending: number;
}

/** What approve answers: the row in its new state, plus the operator the approval created. */
export interface ApproveAccessRequestResult {
  readonly request: AccessRequestView;
  readonly operatorId: string;
}

/**
 * Body for approve, `approveAccessRequestSchema`. `.strict()` upstream, so an unknown key is a 400.
 *
 * ⛔ **`role` IS REQUIRED AND THERE IS NO DEFAULT ANYWHERE IN THE CHAIN**, which is the rule
 * `CreateInvitationBody` records with its measurement: the invitation default was `platform_viewer`,
 * a site-wide role holding `cp:app:runtime:read` and all three `cp:observability:read-*`, and
 * *"not sending a field is not an act anyone reviews; naming a role is."* Approving an access
 * request mints exactly the same kind of principal, so it inherits exactly the same rule — ⛔ do
 * not add a default here, and do not pre-select one in the picker either (D27).
 *
 * ⚠️ `displayName` is the console's, not the provider's. D18 keeps the provider's `name` claim out
 * of `access_requests` entirely, so there is nothing stored to prefill from — the dialog derives a
 * starting value from the address and the administrator edits it.
 */
export interface ApproveAccessRequestBody {
  readonly role: UserRole;
  readonly displayName: string;
}

/** The permission behind the list and the summary — the SAME gate the People rail item carries. */
export const ACCESS_REQUEST_READ_PERMISSION = OPERATOR_READ_PERMISSION;

/**
 * The permission behind approve, deny and reopen.
 *
 * ⛔ **`cp:operator:invite`, REUSED RATHER THAN A NEW `cp:operator:approve` SLUG** (D7). Approving
 * a request creates an operator, which is the authority an invitation already carries — so the
 * two write controls in the access-request section hide together with Invite and
 * Revoke-invitation, exactly as `revokeInvitation`'s docblock describes for its own pair.
 *
 * ⚠️ Approve carries a SECOND, service-level check the console cannot see: the producer resolves a
 * fresh permission snapshot and requires `cp:permission:grant` before it writes anything, because
 * approval also assigns a role. A principal holding `cp:operator:invite` alone therefore sees the
 * control and gets a 403 from it. That is deliberate and it is why the 403 arm has its own copy —
 * the alternative, gating the control on both slugs in the console, would put a second, drifting
 * copy of the producer's rule in the browser.
 */
export const ACCESS_REQUEST_WRITE_PERMISSION = OPERATOR_INVITE_PERMISSION;

export interface AccessRequestsQuery {
  readonly status?: AccessRequestStatus;
}

/**
 * ## ⛔⛔ EVERY ROUTE BELOW IS SPELLED AS A **BARE `/v1/…` LITERAL**, NOT BUILT FROM THE CONSTANT
 *
 * The obvious spelling is `` `${ACCESS_REQUESTS_PATH}/summary` ``, and it makes the route INVISIBLE
 * to `src/dev/__tests__/fixture-routes-answer.test.ts` — the gate that answers *"is every route this
 * app calls served by the owner's review harness?"*. That gate greps this directory for literals
 * that START with `/v1/`, so a path assembled from a constant is not seen at all. ⇒ it reported the
 * harness fine while three of these four routes were unserved, which is a gate returning the wrong
 * answer rather than a missing one.
 *
 * ⛔ **THE PREFIX IS STILL DECLARED ONCE**, as {@link ACCESS_REQUESTS_PATH}, and
 * `accessRequests.api.test.ts` asserts every literal below begins with it. ⇒ drift is caught by a
 * test instead of prevented by construction — which is the trade this module makes deliberately,
 * because the alternative buys one kind of safety by deleting another.
 *
 * ⚠️ Do not "tidy" these back into interpolations of the constant. That edit typechecks, passes
 * every test in this file, and silently removes four routes from the harness gate's obligation.
 */

/**
 * ⛔ An omitted filter is OMITTED, never `?status=undefined`. The upstream schema is `.strict()`
 * with `z.enum(ACCESS_REQUEST_STATUSES).optional()`, so the string `"undefined"` is an unknown
 * VALUE and answers 400 — the same trap `invitationsPath` records.
 */
export function accessRequestsPath(query: AccessRequestsQuery = {}): string {
  return query.status === undefined
    ? '/v1/admin/access-requests'
    : `/v1/admin/access-requests?status=${encodeURIComponent(query.status)}`;
}

export function accessRequestSummaryPath(): string {
  return '/v1/admin/access-requests/summary';
}

/**
 * The two write children, each a bare literal for the reason the block above states.
 *
 * ⛔ Two functions rather than one taking a verb. A `writePath(id, 'approve')` would build the
 * route from a parameter, which puts it back outside the gate's sight — the exact shape this whole
 * block exists to avoid, reintroduced as a tidy-up.
 */
export function approveAccessRequestPath(requestId: string): string {
  return `/v1/admin/access-requests/${encodeURIComponent(requestId)}/approve`;
}

export function denyAccessRequestPath(requestId: string): string {
  return `/v1/admin/access-requests/${encodeURIComponent(requestId)}/deny`;
}

/** ⛔ A bare ARRAY, `created_at DESC`. No envelope and no pagination — the 200-row cap is the bound. */
export function listAccessRequests(
  query: AccessRequestsQuery = {},
): Promise<readonly AccessRequestView[]> {
  return api.get<readonly AccessRequestView[]>(accessRequestsPath(query));
}

export function getAccessRequestSummary(): Promise<AccessRequestSummary> {
  return api.get<AccessRequestSummary>(accessRequestSummaryPath());
}

export function approveAccessRequest(
  requestId: string,
  body: ApproveAccessRequestBody,
): Promise<ApproveAccessRequestResult> {
  return api.post<ApproveAccessRequestResult>(approveAccessRequestPath(requestId), body);
}

/**
 * ⛔ **NO BODY, and the absence is the contract.** The producer takes the operator from the bearer
 * token's `@CurrentUser()` and stamps `decided_by` from it; a body carrying who denied it would be
 * a caller-supplied identity on a privilege write. `reactivateUser` sends nothing for the same
 * reason and is the shape this follows — `api.post(path)` with the second argument omitted, which
 * the client turns into a request with no `body` at all rather than one carrying `"null"`.
 */
export function denyAccessRequest(requestId: string): Promise<AccessRequestView> {
  return api.post<AccessRequestView>(denyAccessRequestPath(requestId));
}
